import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { HotelReservation, ItemPricing, useUpdateTripItem } from "@/hooks/useTrips";
import { toast } from "sonner";
import { PriceInputSection } from "../PriceInputSection";
import { Plus, Trash2 } from "lucide-react";

const hotelSchema = z.object({
  hotel_name: z.string().min(1, "Hotel name is required"),
  address: z.string().optional(),
  city: z.string().optional(),
  check_in_date: z.string().min(1, "Check-in date is required"),
  check_in_time: z.string().optional(),
  check_out_date: z.string().min(1, "Check-out date is required"),
  check_out_time: z.string().optional(),
  room_type: z.string().optional(),
  confirmation_number: z.string().optional(),
});

type HotelFormData = z.infer<typeof hotelSchema>;

interface EditHotelDialogProps {
  hotel: HotelReservation;
  tripId: string;
  index: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditHotelDialog = ({
  hotel,
  tripId,
  index,
  open,
  onOpenChange,
}: EditHotelDialogProps) => {
  const updateItem = useUpdateTripItem();
  const [pricing, setPricing] = useState<ItemPricing | undefined>(hotel.pricing);
  const [guests, setGuests] = useState<{ name: string }[]>(hotel.guests || []);

  const form = useForm<HotelFormData>({
    resolver: zodResolver(hotelSchema),
    defaultValues: {
      hotel_name: hotel.hotel_name || "",
      address: hotel.address || "",
      city: hotel.city || "",
      check_in_date: hotel.check_in_date || "",
      check_in_time: hotel.check_in_time || "",
      check_out_date: hotel.check_out_date || "",
      check_out_time: hotel.check_out_time || "",
      room_type: hotel.room_type || "",
      confirmation_number: hotel.confirmation_number || "",
    },
  });

  const addGuest = () => {
    setGuests([...guests, { name: "" }]);
  };

  const removeGuest = (idx: number) => {
    setGuests(guests.filter((_, i) => i !== idx));
  };

  const updateGuest = (idx: number, value: string) => {
    const updated = [...guests];
    updated[idx] = { name: value };
    setGuests(updated);
  };

  const onSubmit = (data: HotelFormData) => {
    // Filter out empty guests
    const validGuests = guests.filter(g => g.name.trim());

    // Preserve enrichment data when editing
    const updatedHotel: HotelReservation = {
      ...hotel,
      ...data,
      pricing,
      guests: validGuests,
    };

    updateItem.mutate(
      {
        tripId,
        itemType: "hotels",
        index,
        item: updatedHotel,
      },
      {
        onSuccess: () => {
          toast.success("Hotel updated");
          onOpenChange(false);
        },
        onError: (error) => {
          toast.error(`Failed to update: ${error.message}`);
        },
      }
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Hotel</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
            {/* Hotel Info Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Hotel Information</h3>
              <FormField
                control={form.control}
                name="hotel_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hotel Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Marriott Downtown" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <Input placeholder="123 Main St" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City</FormLabel>
                    <FormControl>
                      <Input placeholder="New York, NY" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="room_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Room Type</FormLabel>
                      <FormControl>
                        <Input placeholder="King Suite" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmation_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirmation #</FormLabel>
                      <FormControl>
                        <Input placeholder="ABC123" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Check-in Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Check-in</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="check_in_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="check_in_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Check-out Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground">Check-out</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="check_out_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="check_out_time"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            {/* Guests Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-muted-foreground">Guests</h3>
                <Button type="button" variant="outline" size="sm" onClick={addGuest}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Guest
                </Button>
              </div>
              {guests.length === 0 ? (
                <p className="text-sm text-muted-foreground">No guests added</p>
              ) : (
                <div className="space-y-2">
                  {guests.map((guest, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input
                        placeholder="Guest Name"
                        value={guest.name}
                        onChange={(e) => updateGuest(idx, e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 text-muted-foreground hover:text-destructive"
                        onClick={() => removeGuest(idx)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Separator />

            {/* Pricing Section */}
            <PriceInputSection
              pricing={pricing}
              onChange={setPricing}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateItem.isPending}>
                {updateItem.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};
